'use client';

import { createContext, useContext, useRef, useSyncExternalStore } from 'react';

import { AlertBanner } from './AlertBanner';
import { createAlertStore } from './alertStore';
import { Alert } from './types';

type IAlertBannerContext = {
    store: ReturnType<typeof createAlertStore>;
};

const AlertBannerContext = createContext<IAlertBannerContext | null>(null);
AlertBannerContext.displayName = 'AlertBannerContext';

type Props = {
    children?: React.ReactNode;
};

export function AlertBannerProvider({ children }: Props) {
    const store = useRef(createAlertStore());

    return (
        <AlertBannerContext.Provider
            value={{
                store: store.current,
            }}
        >
            {children}
        </AlertBannerContext.Provider>
    );
}

export const useAlertBanner = () => {
    const context = useContext(AlertBannerContext);

    if (!context) throw Error(`${AlertBannerContext.displayName} is missing`);

    const { store } = context;

    const alerts = useSyncExternalStore(
        store.subscribe,
        store.getAlerts,
        store.getServerAlerts,
    );

    const open = (alert: Alert) => {
        const added = store.add(alert);
        let alertTimeout = 5000; // Changed for debugging - OG 5000 DEbug 30000
        if (alert.isRefreshVisible) {
            alertTimeout = 10000; // Changed for debugging - OG 10000 DEbug 30000
        }

        // Start the exit animation before removing from DOM
        setTimeout(() => {
            store.startRemove(added.id);

            // Wait for animation to complete before removing from the store
            setTimeout(() => {
                store.remove(added.id);
            }, 500); // Animation duration
        }, alertTimeout);

        return () => {
            store.startRemove(added.id);
            setTimeout(() => {
                store.remove(added.id);
            }, 500); // Animation duration
        };
    };

    return {
        alerts,
        info: (message?: string, isRefreshVisible?: boolean) =>
            open({
                type: 'info',
                message: message ?? '',
                isRefreshVisible: isRefreshVisible ?? false,
            }),
        warning: (message?: string) =>
            open({
                type: 'warning',
                message: message ?? '',
                isRefreshVisible: false,
            }),
        error: (message?: string) =>
            open({
                type: 'error',
                message: message ?? '',
                isRefreshVisible: false,
            }),
    };
};

export function AlertBannerZone() {
    const { alerts } = useAlertBanner();

    return (
        <section className="AlertBannerZone">
            {alerts.map((alert: any) => (
                <AlertBanner
                    key={alert.id}
                    type={alert.type}
                    message={alert.message}
                    isRefreshVisible={alert.isRefreshVisible}
                    isExiting={alert.isExiting}
                />
            ))}
        </section>
    );
}
